import { uniqBy } from "lodash";
import * as React from "react";
import { Link } from "react-router-dom";
import { OscillometryAnalysisSet, WebPortalClient } from "webportalclient";
import { DateMatchSortHeader } from "../../Components/DateMatchSortHeader";
import { MatchSortHeader } from "../../Components/MatchSortHeader";
import { useQuery } from "../../Components/Query";
import { Column, Table } from "../../Components/QueryTable";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { useReferenceContextProvider } from "../../Core/ReferenceContextProvider";
import { useSecurityContext } from "../../Core/SecurityContext";
import { OscillometryAnalysisSetTypeEnum } from "../../misc/api/ApiOscillometryAnalysisSetTypeEnum";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { SessionTypes } from "../../misc/api/ApiSessionTypes";
import { OverreadStatusIdEnum, Permission, User } from "../../misc/api/apiTypes";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import { AnalysisAcceptabilityStatus } from "../../misc/components/analysis/AnalysisAcceptabilityStatus";
import { AssignToUserInGroupModal } from "../../misc/components/AssignToUserInGroupModal";
import { Authorization } from "../../misc/components/Authorization";
import Condition from "../../misc/components/Condition";
import { formatDateTime } from "../../misc/components/Formatters/Formatters";
import { HamburgerMenu } from "../../misc/components/HamburgerMenu";
import { CreateAnalysisBatchModal } from "../../PulmonaryFunctionTests/QC/CreateAnalysisBatchModal";

export const OscillometryAnalysisListTable = ({ }) => {
    const client = useWebPortalApiClient();
    const { study } = useCurrentStudyConfigurationContext();
    const { overreadStatuses } = useReferenceContextProvider();
    const { currentUser, currentUserPermissions } = useSecurityContext();
    const [siteOptions, setSiteOptions] = React.useState([]);
    const [subjectOptions, setSubjectOptions] = React.useState([]);
    const [visitOptions, setVisitOptions] = React.useState([]);
    const [sessionOptions, setSessionOptions] = React.useState([]);
    const [analystSetTypes, setAnalystSetTypes] = React.useState([]);
    const [analysts, setAnalysts] = React.useState([]);
    const [qcBatchSaved, setQcBatchSaved] = React.useState<boolean | undefined>(undefined);
    const [qcSelectionModalVisible, setQCSelectionModelVisible] = React.useState(false);
    const [sessionIdToReassign, setSessionIdToReassign] = React.useState(null);
    const [isReassignToUserModalVisible, setReassignToUserModalVisible] = React.useState(false);
    const { data, applyFilters, refresh } = useQuery();
    const [isAssignToUserModalVisible, setAssignToUserModalVisible] = React.useState(false);
    const [sessionIdToSendForQC, setSessionIdToSendForQC] = React.useState(null);
    const legacyClient = useWebPortalLegacyClient();

    React.useEffect(() => {
        const getSites = async () => {

            const sites = await client.client.getSites("id,number", `study/id eq ${study.id}`, "number");
            const options = sites.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSiteOptions(options);
        };


        const getSubjects = async () => {

            const subjects = await client.client.getSubjects("id,number", `site/study/id eq ${study.id}`, "number");
            const options = subjects.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSubjectOptions(options);
        };

        const getVisits = async () => {

            const visits = await client.client.getOscillometrySessions(undefined, `visit/subject/site/study/id eq ${study.id}`, "visit/name", "visit");
            const options = uniqBy(visits.value, o => o.visit.name).map(s => ({
                value: s.visit.name,
                label: s.visit.name,
            }));

            setVisitOptions(options);
        };

        const getSessions = async () => {

            const sessions = await client.client.getOscillometrySessions("name", `visit/subject/site/study/id eq ${study.id}`, "name");
            const options = uniqBy(sessions.value, o => o.name).map(s => ({
                value: s.name,
                label: s.name,
            }));

            setSessionOptions(options);
        };

        const getAnalystSetTypes = async () => {
            const types = await legacyClient.getOscillometryAnalysisSetTypes();
            const options = uniqBy(types, o => o.Id).map(s => ({
                value: s.Id,
                label: s.Name,
            }));

            setAnalystSetTypes(options);
        };

        const getAnalysts = async () => {

            const analysts = await client.client.getOscillometryAnalysisSets("analyst", `session/visit/subject/site/studyId eq ${study.id}`, "analyst");

            const options = uniqBy(analysts.value, o => o.analyst).filter(o => o.analyst).map(s => ({
                value: s.analyst,
                label: s.analyst,
            }));

            setAnalysts(options);
        };

        getSites();
        getSubjects();
        getVisits();
        getSessions();
        getAnalystSetTypes();
        getAnalysts();
    }, [study.id]);

    const clearAllFilters = () => {
        applyFilters([]);
    }

    const statusesOptions = overreadStatuses.map(os => ({
        value: os.id,
        label: os.name
    }))

    const isQcAnalysisSet = (set: OscillometryAnalysisSet): boolean => set.type === OscillometryAnalysisSetTypeEnum.QC;

    const formatSiteId = set => isQcAnalysisSet(set) ? '-' : set.session.visit.subject.site.number;
    const formatSubjectId = set => isQcAnalysisSet(set) ? '-' : set.session.visit.subject.number;
    const formatVisitLabel = set => isQcAnalysisSet(set) ? '-' : set.session.visit.name;
    const formatSessionLabel = set => isQcAnalysisSet(set) ? '-' : set.session.name;

    const formatAnalyst = analyst => analyst ? analyst : "-";

    const formatStatus = (status: string) => {
        return (<>
            <AnalysisAcceptabilityStatus value={status} /><br />
        </>);
    }

    const formatAnalysisSetType = (type: string) => {
        switch (type) {
            case OscillometryAnalysisSetTypeEnum.Overread:
                return "Overread";
            case OscillometryAnalysisSetTypeEnum.Site:
                return "Site";
            case OscillometryAnalysisSetTypeEnum.QC:
                return "QC";
        }
    }

    const handleQcBatchSavingAlertMessage = () => {
        if (qcBatchSaved) {
            return (<div className="row">
                <div className="col-md-12">
                    <div className="alert alert-success">
                        <i className="fa fa-check icon"></i>
                        QC batch is created successfully.
                    </div>
                </div>
            </div>);
        }
        else if (qcBatchSaved === false) {
            return (<div className="row">
                <div className="col-md-12">
                    <div className="alert alert-danger">
                        <i className="fa fa-times icon"></i>
                        There was an error. QC batch wasn't saved.
                    </div>
                </div>
            </div>);
        }
        else {
            return null
        }
    }

    const handleReassign = async (sessionId: string, userId: string) => {
        await legacyClient.reassignOscillometry(sessionId, userId);
        setReassignToUserModalVisible(false);
        refresh();
    }
    const handleSendForQC = async (sessionId: string, userId: string) => {
        await legacyClient.createOscillometryQC(sessionId, OscillometryAnalysisSetTypeEnum.QC, userId);
        setAssignToUserModalVisible(false);
        refresh();
    }

    const RenderHamburgerMenu = (analysisSet: OscillometryAnalysisSet, currentPermissions: Permission[], currentStudyId: string) => {
        return (
            <HamburgerMenu automationId={`options_${analysisSet.id}`} >
                <Authorization permissionId={PermissionEnum.AccessSingleOscillometrySession} studyId={currentStudyId} currentPermissions={currentPermissions}>
                    <Condition test={() => (analysisSet.type !== OscillometryAnalysisSetTypeEnum.QC) || (analysisSet.type === OscillometryAnalysisSetTypeEnum.QC && analysisSet.analyst === currentUser.Email)}>
                        <li>
                            <Link data-automationid={`ViewLink_${analysisSet.id}`} to={`/Oscillometry/Analysis/${analysisSet.id}/view`}>View</Link>
                        </li>
                    </Condition>
                </Authorization>
                <Authorization permissionId={PermissionEnum.OverreadOscillometrySession} studyId={currentStudyId} currentPermissions={currentPermissions}>
                    <Condition test={() => analysisSet !== null && analysisSet.type !== OscillometryAnalysisSetTypeEnum.Site && (!analysisSet.analyst || analysisSet.analyst === currentUser.Email)}>
                        <li>
                            <Link data-automationid={`OverreadLink_${analysisSet.id}`} to={`/Oscillometry/Analysis/${analysisSet.id}/overread`}>Over-read</Link>
                        </li>
                    </Condition>
                </Authorization>
                <Authorization permissionId={PermissionEnum.CreateAQCOscillometryAnalysisSet}
                    studyId={currentStudyId}
                    currentPermissions={currentPermissions}>
                    <Condition test={() => analysisSet.type === OscillometryAnalysisSetTypeEnum.Overread}>
                        <li>
                            <a onClick={() => { setAssignToUserModalVisible(true); setSessionIdToSendForQC(analysisSet.sessionId); }}>
                                Manually Send for QC Review...
                            </a>
                        </li>
                    </Condition>
                </Authorization>
                <Authorization permissionId={PermissionEnum.OverreadOscillometrySession} studyId={currentStudyId} currentPermissions={currentPermissions}>
                    <Condition test={() => analysisSet.type === OscillometryAnalysisSetTypeEnum.Overread && analysisSet.status === OverreadStatusIdEnum.Pending}>
                        <li>
                            <a onClick={() => { setSessionIdToReassign(analysisSet.id); setReassignToUserModalVisible(true); }}>
                                Reassign
                            </a>
                        </li>
                    </Condition>
                </Authorization>
            </HamburgerMenu>
        );
    };

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Oscillometry Analysis Sets</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <button data-automationId="ClearFilters" className="btn btn-primary" onClick={clearAllFilters}>
                            Clear All Filters
                        </button>
                        &nbsp;
                    </div>
                    <div className="h2 pull-right">
                        <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionId={PermissionEnum.CreateAutomatedQCOscillometrySelection}>
                            <button className="btn btn-primary" onClick={() => setQCSelectionModelVisible(true)}>Create QC Batch</button>
                            &nbsp;
                        </Authorization>
                    </div>
                </div>
            </div>
            {handleQcBatchSavingAlertMessage()}
            <div className="row">
                <div className="col-md-12">
                    <div className="ibox">
                        <div className="ibox-content" data-automationId="oscillometryTable">
                            <div className="table-responsive" style={{ minHeight: "900px" }}>
                                <Table data={data}>
                                    <Column field="" header={<MatchSortHeader title="Site ID" field="session/visit/subject/site/id" options={siteOptions} sortField="session/visit/subject/site/number" />} formatter={set => formatSiteId(set)}/>
                                    <Column field="" header={<MatchSortHeader title="Subject ID" field="session/visit/subject/id" options={subjectOptions} sortField="session/visit/subject/number" automationId="SubjectID" />} formatter={set => formatSubjectId(set)} />
                                    <Column field="" header={<MatchSortHeader title="Visit" field="session/visit/name" options={visitOptions} sortField="session/visit/name" />} formatter={set => formatVisitLabel(set)} />
                                    <Column field="" header={<MatchSortHeader title="Session" field="session/name" options={sessionOptions} sortField="session/name" />} formatter={set => formatSessionLabel(set)} />
                                    <Column field="type" header={<MatchSortHeader title="Analysis Set Type" field="type" options={analystSetTypes} sortField="type" automationId="AnalysisType" />}  formatter={type => formatAnalysisSetType(type)} />
                                    <Column field="created" header={<DateMatchSortHeader title="Created At" field="created" sortField="created" />} formatter={created => formatDateTime(created)} />
                                    <Column field="started" header={<DateMatchSortHeader title="Started At" field="started" sortField="started" />} formatter={started => formatDateTime(started)} />
                                    <Column field="analyst" header={<MatchSortHeader title="Overreader" field="analyst" options={analysts} sortField="analyst" />} formatter={analyst => formatAnalyst(analyst)} />
                                    <Column field="status" header={<MatchSortHeader title="Status" field="status" options={statusesOptions} sortField="status" automationId="Status" />} formatter={analysisSet => formatStatus(analysisSet)} />
                                    <Column field="" formatter={analysisSet => RenderHamburgerMenu(analysisSet, currentUserPermissions, study.id)} />
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
           
            <AssignToUserInGroupModal show={isAssignToUserModalVisible} headerText="Create QC Analysis Set &amp; assign to user" permission={PermissionEnum.OverreadOscillometrySession}
                onClose={() => setAssignToUserModalVisible(false)}
                onAssign={(user) => handleSendForQC(sessionIdToSendForQC, user)} />
            {qcSelectionModalVisible ? <CreateAnalysisBatchModal currentUser={currentUser} sessionType={SessionTypes.Oscillometry} minimumTests={3} onSave={(result: boolean) => setQcBatchSaved(result)} onClose={() => setQCSelectionModelVisible(false)} /> : null}

            <AssignToUserInGroupModal show={isReassignToUserModalVisible} headerText="Reassign Analysis Set" permission={PermissionEnum.OverreadOscillometrySession}
                onClose={() => setReassignToUserModalVisible(false)}
                onAssign={(user) => { handleReassign(sessionIdToReassign, user) }} />
        </>
    );
}
